import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@ferro/ui';
import type { ClientConfig } from '@ferro/config';
import { useAuth } from '../contexts/AuthContext.js';
import { roleHasPermission } from '../lib/rbac-matrix.js';
import { OwnerLink } from '../components/owner-link/index.js';
import {
  fetchIncident,
  readSession,
  resolveIncident,
  INCIDENT_STATUSES,
  type IncidentSeverity,
  type IncidentStatus,
  type IncidentSummary,
} from '../lib/api.js';
// Sawyer 12 incident-wording-pet-notes (F3) — the owner's word for the log.
import { useTenantVocabulary } from '../lib/useTenantVocabulary.js';

type IncidentDetailProps = { config: ClientConfig };

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; incident: IncidentSummary };

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};
const SEVERITY_COLOR: Record<IncidentSeverity, string> = {
  low: '#5b6472',
  medium: '#b45309',
  high: '#c2410c',
  critical: '#b91c1c',
};
const STATUS_LABEL: Record<IncidentStatus, string> = {
  open: 'Open',
  in_review: 'In review',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};
const KIND_LABEL: Record<string, string> = {
  bite: 'Bite',
  injury: 'Injury',
  escape: 'Escape',
  illness: 'Illness',
  altercation: 'Altercation',
  property_damage: 'Property damage',
  in_heat: 'In heat',
  other: 'Other',
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

export function IncidentDetail({ config }: IncidentDetailProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { incident: noun } = useTenantVocabulary(apiUrl);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { staff } = useAuth();
  // Owner-only triage mirrors the server gate (incidents.owner-only:write:all)
  // — same permission the server enforces, so we never show a control it 403s.
  const canTriage = roleHasPermission(
    staff?.role ?? null,
    'incidents.owner-only:write:all',
  );

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [status, setStatus] = useState<IncidentStatus>('open');
  const [resolution, setResolution] = useState('');
  const [ownerNotes, setOwnerNotes] = useState('');
  const [customerNotified, setCustomerNotified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const hydrateForm = (i: IncidentSummary): void => {
    setStatus(i.status);
    setResolution(i.resolution ?? '');
    setOwnerNotes(i.owner_notes ?? '');
    setCustomerNotified(i.customer_notified);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const res = await fetchIncident(apiUrl, token, id);
      setState({ kind: 'ready', incident: res.incident });
      hydrateForm(res.incident);
    } catch (err) {
      setState({
        kind: 'error',
        // '' ⇒ the render below supplies "Unable to load <word>" (Sawyer F3).
        message: err instanceof Error ? err.message : '',
      });
    }
  }, [apiUrl, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (saving || !id) return;
    setSaveError(null);
    setSaved(false);
    setSaving(true);
    const { token } = readSession();
    if (!token) {
      setSaveError('Not authenticated');
      setSaving(false);
      return;
    }
    try {
      const res = await resolveIncident(apiUrl, token, id, {
        status,
        resolution: resolution.trim(),
        ownerNotes: ownerNotes.trim(),
        customerNotified,
      });
      setState({ kind: 'ready', incident: res.incident });
      hydrateForm(res.incident);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">{noun.one}</h1>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => navigate('/incidents')}
        >
          ← Back to {noun.manyLower}
        </button>
      </header>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading {noun.oneLower}…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message || `Unable to load ${noun.oneLower}`}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready'
        ? (() => {
            const i = state.incident;
            return (
              <>
                <div
                  className="ferro-detail__meta"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}
                >
                  <span
                    className="ferro-badge"
                    style={{ backgroundColor: SEVERITY_COLOR[i.severity], color: '#fff' }}
                  >
                    {SEVERITY_LABEL[i.severity]}
                  </span>
                  <span className="ferro-badge">{STATUS_LABEL[i.status]}</span>
                  <strong>{KIND_LABEL[i.kind] ?? i.kind}</strong>
                  <span className="ferro-muted">
                    {i.dog_name ?? 'Unknown dog'} ·{' '}
                    {i.customer_name ? (
                      <OwnerLink name={i.customer_name} customerId={i.customer_id} />
                    ) : (
                      '—'
                    )}
                  </span>
                </div>

                <dl className="ferro-dog-card__facts" style={{ marginTop: '1rem' }}>
                  <div className="ferro-dog-card__fact">
                    <dt>Occurred</dt>
                    <dd>{formatDateTime(i.occurred_at)}</dd>
                  </div>
                  <div className="ferro-dog-card__fact">
                    <dt>Filed by</dt>
                    <dd>{i.reporter_name ?? '—'}</dd>
                  </div>
                  <div className="ferro-dog-card__fact">
                    <dt>Customer notified</dt>
                    <dd>{i.customer_notified ? 'Yes' : 'No'}</dd>
                  </div>
                  <div className="ferro-dog-card__fact ferro-dog-card__fact--wide">
                    <dt>What happened</dt>
                    <dd>{i.narrative}</dd>
                  </div>
                </dl>

                {i.photo_urls.length > 0 ? (
                  <div
                    style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}
                  >
                    {i.photo_urls.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`${noun.one} ${idx + 1}`}
                        style={{ maxWidth: 200, borderRadius: 'var(--ferro-radius, 8px)' }}
                      />
                    ))}
                  </div>
                ) : null}

                {/* Owner-only liability + triage. The fields below are only
                    present in the API response for owner-tier callers; the
                    server strips them otherwise. */}
                {canTriage ? (
                  <form
                    className="ferro-form"
                    onSubmit={handleSave}
                    noValidate
                    style={{ marginTop: '1.5rem' }}
                  >
                    <h2 className="ferro-section__title" style={{ fontSize: '1.1rem' }}>
                      Triage &amp; resolution (business owners only)
                    </h2>

                    <label className="ferro-field">
                      <span className="ferro-field__label">Status</span>
                      <select
                        className="ferro-field__input"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as IncidentStatus)}
                      >
                        {INCIDENT_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="ferro-field">
                      <span className="ferro-field__label">Resolution</span>
                      <textarea
                        className="ferro-field__input ferro-field__input--textarea"
                        rows={3}
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Outcome / how it was handled."
                      />
                    </label>

                    <label className="ferro-field">
                      <span className="ferro-field__label">Internal note (business owners only)</span>
                      <textarea
                        className="ferro-field__input ferro-field__input--textarea"
                        rows={3}
                        value={ownerNotes}
                        onChange={(e) => setOwnerNotes(e.target.value)}
                        placeholder="Liability / legal follow-up."
                      />
                    </label>

                    <label className="ferro-checkbox">
                      <input
                        type="checkbox"
                        checked={customerNotified}
                        onChange={(e) => setCustomerNotified(e.target.checked)}
                      />
                      <span>Customer has been notified</span>
                    </label>

                    {saveError ? <Alert variant="error">{saveError}</Alert> : null}
                    {saved ? (
                      <p className="ferro-form__success" role="status">
                        Saved.
                      </p>
                    ) : null}

                    <div className="ferro-form__actions">
                      <button
                        type="submit"
                        className="ferro-btn ferro-btn--primary"
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : null}
              </>
            );
          })()
        : null}
    </section>
  );
}
